// app/javascript/controllers/ats_resume_library_controller.js
//
// Resume Library panel: list + upload + Master/Latest chips + set-master +
// delete. Master/Latest come from the real server flags (is_master /
// is_latest in the list payload) — NOT a created_at heuristic — so the chip
// reflects the actual master the backend uses.
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["list", "empty", "uploadInput", "error", "busy"]
  static values = {
    listEndpoint:     { type: String, default: "/api/v1/ats_optimizer/resumes/list" },
    uploadEndpoint:   { type: String, default: "/api/v1/ats_optimizer/resumes/upload" },
    selectedResumeId: String
  }

  connect() { this.load() }

  async load() {
    try {
      const r = await fetch(this.listEndpointValue, { headers: { "Accept": "application/json" } })
      if (!r.ok) throw new Error(`list HTTP ${r.status}`)
      const data = await r.json()
      this.renderList(data.data || [])
    } catch (e) {
      this.renderList([])
      console.warn("[ats-resume-library] load failed:", e.message)
    }
  }

  renderList(resumes) {
    this.listTarget.replaceChildren()
    this.emptyTarget.classList.toggle("hidden", resumes.length > 0)
    if (!resumes.length) return

    // Auto-select the master (or, failing that, the first row) when nothing
    // is selected yet — so the preview hydrates on load.
    if (!this.selectedResumeIdValue) {
      const initial = resumes.find(r => r.is_master) || resumes[0]
      if (initial) {
        this.selectedResumeIdValue = initial.resume_id
        this.dispatch("resume-selected", { detail: { resumeId: initial.resume_id }, prefix: "ats-optimizer" })
      }
    }

    for (const r of resumes) {
      this.listTarget.appendChild(this.#row(r))
    }
  }

  #row(resume) {
    const li = document.createElement("li")
    li.dataset.resumeId = resume.resume_id
    li.className =
      "group/row flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors " +
      (resume.resume_id === this.selectedResumeIdValue
        ? "border-primary/30 bg-primary-fixed/10"
        : "border-border-light hover:bg-surface-container-lowest")
    li.addEventListener("click", () => this.#select(resume.resume_id))

    li.innerHTML = `
      <div class="flex items-center gap-3 min-w-0">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
             class="size-4 text-primary shrink-0">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
        </svg>
        <div class="min-w-0">
          <p class="font-label-lg text-label-lg text-on-surface truncate">${this.#escape(resume.filename || "Untitled resume")}</p>
          <p class="font-label-sm text-label-sm text-on-surface-variant flex items-center gap-1">
            <span class="size-1.5 rounded-full bg-primary-container"></span> Ready
          </p>
        </div>
      </div>
      <div class="flex items-center gap-1 shrink-0">
        <div class="flex gap-1" data-chips></div>
        <button type="button"
                class="ml-1 px-2 py-1 rounded text-[10px] font-medium text-on-surface-variant border border-border-light
                       hover:text-primary hover:border-primary/50 transition-colors
                       opacity-0 group-hover/row:opacity-100 focus:opacity-100 ${resume.is_master ? "hidden" : ""}"
                data-make-master>Set master</button>
        <button type="button"
                class="p-1.5 rounded text-on-surface-variant hover:text-error hover:bg-error-container/40 transition-colors opacity-0 group-hover/row:opacity-100 focus:opacity-100"
                aria-label="Delete resume"
                data-delete>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
               class="size-4">
            <path d="M3 6h18"/>
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
            <line x1="10" y1="11" x2="10" y2="17"/>
            <line x1="14" y1="11" x2="14" y2="17"/>
          </svg>
        </button>
      </div>
    `

    // Chips reflect the REAL server flags, not a created_at guess.
    const chips = li.querySelector("[data-chips]")
    if (resume.is_master) chips.appendChild(this.#chip("Master", "neutral"))
    if (resume.is_latest && !resume.is_master) chips.appendChild(this.#chip("Latest", "primary"))

    li.querySelector("[data-make-master]")?.addEventListener("click", e => {
      e.stopPropagation()
      this.#makeMaster(resume.resume_id, resume.filename)
    })
    li.querySelector("[data-delete]").addEventListener("click", e => {
      e.stopPropagation()
      this.#deleteResume(resume.resume_id, resume.filename)
    })

    return li
  }

  async #makeMaster(resumeId, filename) {
    this.#clearError()
    try {
      const r = await fetch(
        `/api/v1/ats_optimizer/resumes/${encodeURIComponent(resumeId)}/make-master`,
        { method: "POST", headers: { "Accept": "application/json", "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content } }
      )
      if (!r.ok) throw new Error(await this.#detail(r))
      await this.load()
    } catch (e) {
      this.#showError(`Couldn't set master: ${e.message}`)
    }
  }

  async #deleteResume(resumeId, filename, force = false) {
    if (!force && !window.confirm(`Delete "${filename || "this resume"}"? This cannot be undone.`)) return
    this.#clearError()
    try {
      const url = `/api/v1/ats_optimizer/resumes/${encodeURIComponent(resumeId)}${force ? "?force=true" : ""}`
      const r = await fetch(url, {
        method: "DELETE",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content
        }
      })
      if (!r.ok) {
        const body = await r.clone().json().catch(() => ({}))
        // Resume has optimization history — offer to delete it + the history.
        if (!force && r.status === 409 && body.code === "has_runs") {
          if (window.confirm(`"${filename || "This resume"}" has optimization history (cover letters, JD matches). Delete the resume AND its history?`)) {
            return this.#deleteResume(resumeId, filename, true)
          }
          return
        }
        throw new Error(body.detail || `HTTP ${r.status}`)
      }
      // If the deleted resume was selected, clear selection so the coordinator
      // stops referencing a dead id.
      if (this.selectedResumeIdValue === resumeId) {
        this.selectedResumeIdValue = ""
        this.dispatch("resume-selected", { detail: { resumeId: "" }, prefix: "ats-optimizer" })
      }
      await this.load()
    } catch (e) {
      console.warn("[ats-resume-library] delete failed:", e.message)
      this.#showError(`Delete failed: ${e.message}`)
    }
  }

  async #detail(response) {
    const body = await response.clone().json().catch(() => ({}))
    return body.detail || `HTTP ${response.status}`
  }

  #chip(label, variant) {
    const span = document.createElement("span")
    const cls = variant === "primary"
      ? "px-2 py-1 bg-secondary-container/50 text-secondary border border-secondary/20 rounded text-[10px] font-medium"
      : "px-2 py-1 bg-surface-container-low border border-border-light rounded text-[10px] font-medium text-text-muted"
    span.className = cls
    span.textContent = label
    return span
  }

  #select(resumeId) {
    this.selectedResumeIdValue = resumeId
    this.dispatch("resume-selected", { detail: { resumeId }, prefix: "ats-optimizer" })
    // Re-render to update active styling.
    this.load()
  }

  async uploadResume(event) {
    const file = event.target.files?.[0]
    if (!file) return
    this.#clearError()
    this.#setBusy(true)
    const form = new FormData()
    form.append("file", file)
    try {
      const r = await fetch(this.uploadEndpointValue, {
        method: "POST",
        headers: { "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content },
        body: form
      })
      if (!r.ok) {
        const msg = await this.#parseErrorMessage(r)
        throw new Error(msg)
      }
      await this.load()
    } catch (e) {
      console.warn("[ats-resume-library] upload failed:", e.message)
      this.#showError(e.message || "Upload failed")
    } finally {
      this.#setBusy(false)
      event.target.value = ""
    }
  }

  async #parseErrorMessage(response) {
    try {
      const data = await response.clone().json()
      if (data?.detail) {
        // Server's detail looks like "ATS-Optimizer API error: 422 Failed to parse..."
        // Strip the API-error prefix for a cleaner user-facing message.
        return String(data.detail).replace(/^ATS-Optimizer API error:\s*\d+\s*/i, "")
      }
    } catch (_e) {}
    return `Upload failed (HTTP ${response.status})`
  }

  #showError(msg) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = msg
    this.errorTarget.classList.remove("hidden")
  }

  #clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }

  #setBusy(on) {
    if (!this.hasBusyTarget) return
    this.busyTarget.classList.toggle("hidden", !on)
  }

  #escape(s) { return String(s).replace(/[&<>"']/g, c => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;" }[c])) }
}
